import { IDrawing } from '../drawing-queue/drawing.interface';

export interface DrawingValidationResult {
  isValid: boolean;
  error?: string;
  isMalformed?: boolean;
}

/**
 * Validates if drawing data has the required structure for rendering
 */
export function validateDrawingData(drawingData: any): DrawingValidationResult {
  if (!drawingData) {
    return {
      isValid: false,
      error: 'Drawing data is null or undefined',
      isMalformed: true
    };
  }

  // Check if drawing data is a string (malformed case)
  if (typeof drawingData === 'string') {
    return {
      isValid: false,
      error: `Drawing data is malformed (string): "${drawingData}"`,
      isMalformed: true
    };
  }

  // Check if drawing data is not an object
  if (typeof drawingData !== 'object') {
    return {
      isValid: false,
      error: `Drawing data is not an object (type: ${typeof drawingData})`,
      isMalformed: true
    };
  }

  // Check required properties exist
  const requiredProps = ['canvasWidth', 'canvasHeight', 'clickX', 'clickY', 'clickDrag', 'clickColor', 'clickWidth'];
  const missingProps = requiredProps.filter(prop => !(prop in drawingData));
  
  if (missingProps.length > 0) {
    return {
      isValid: false,
      error: `Missing required properties: ${missingProps.join(', ')}`,
      isMalformed: true
    };
  }

  // Check if arrays have the same length
  const { clickX, clickY, clickDrag, clickColor, clickWidth } = drawingData;
  
  if (!Array.isArray(clickX) || !Array.isArray(clickY) || !Array.isArray(clickDrag) || 
      !Array.isArray(clickColor) || !Array.isArray(clickWidth)) {
    return {
      isValid: false,
      error: 'Click data properties must be arrays',
      isMalformed: true
    };
  }

  const expectedLength = clickX.length;
  if (clickY.length !== expectedLength || clickDrag.length !== expectedLength || 
      clickColor.length !== expectedLength || clickWidth.length !== expectedLength) {
    return {
      isValid: false,
      error: `Array lengths don't match: clickX(${clickX.length}), clickY(${clickY.length}), clickDrag(${clickDrag.length}), clickColor(${clickColor.length}), clickWidth(${clickWidth.length})`,
      isMalformed: true
    };
  }

  // Check for valid canvas dimensions
  if (typeof drawingData.canvasWidth !== 'number' || typeof drawingData.canvasHeight !== 'number' ||
      drawingData.canvasWidth <= 0 || drawingData.canvasHeight <= 0) {
    return {
      isValid: false,
      error: 'Invalid canvas dimensions',
      isMalformed: true
    };
  }

  return { isValid: true };
}

/**
 * Checks if a drawing is malformed and should be flagged
 */
export function isDrawingMalformed(drawing: IDrawing): boolean {
  const validation = validateDrawingData(drawing.DrawingData);
  return validation.isMalformed || false;
}

/**
 * Gets a safe error message for display
 */
export function getDrawingErrorMessage(drawing: IDrawing): string {
  const validation = validateDrawingData(drawing.DrawingData);
  return validation.error || 'Unknown drawing data error';
}

/**
 * Safely renders drawing data with error handling
 */
export function safeRenderDrawing(
  context: CanvasRenderingContext2D,
  drawingData: any,
  width: number,
  height: number,
  onError?: (error: string) => void
): boolean {
  const validation = validateDrawingData(drawingData);
  
  if (!validation.isValid) {
    if (onError) {
      onError(validation.error || 'Unknown error');
    }
    
    // Render error indicator
    context.fillStyle = '#ff0000';
    context.font = '16px Arial';
    context.textAlign = 'center';
    context.fillText('⚠️ MALFORMED DRAWING', width / 2, height / 2 - 10);
    context.font = '12px Arial';
    context.fillText(validation.error || 'Invalid data', width / 2, height / 2 + 10);
    
    return false;
  }

  // Render valid drawing
  try {
    const scaleX = width / drawingData.canvasWidth;
    const scaleY = height / drawingData.canvasHeight;

    for (let i = 0; i < drawingData.clickX.length; i++) {
      context.beginPath();
      if (drawingData.clickDrag[i] && i) {
        context.moveTo(drawingData.clickX[i - 1] * scaleX, drawingData.clickY[i - 1] * scaleY);
      } else {
        context.moveTo(drawingData.clickX[i] * scaleX - 1, drawingData.clickY[i] * scaleY);
      }
      context.lineTo(drawingData.clickX[i] * scaleX, drawingData.clickY[i] * scaleY);
      context.strokeStyle = drawingData.clickColor[i];
      context.lineWidth = drawingData.clickWidth[i] * scaleX;
      context.lineJoin = 'round';
      context.closePath();
      context.stroke();
    }
    return true;
  } catch (error) {
    if (onError) {
      onError(`Rendering error: ${error}`);
    }
    return false;
  }
}